import { useEffect, useMemo, useState } from 'react'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  loadCoordinateInfo,
  loadModelInfo,
  type CoordinateInfo,
  type ModelInfo,
} from '@viewer/lib/modelInfo'
import { rawIfc } from '@viewer/lib/rawIfc'
import IfcxElementoView from './IfcxElementoView'
import {
  decodeRawElement,
  type RawElementInfo,
} from '@viewer/lib/ifcRawParse'
import {
  findGuid,
  getAttributes,
  getCategory,
  getGeometry,
  getInverseRelations,
  getLocation,
  getMaterialsAndClassifications,
  getPsetsAndQsets,
  getType,
  podeTerType,
  formatValue,
  type KeyValue,
  type PsetGroup,
} from '@viewer/lib/ifcItemData'
import { useUnidadeDe } from './useUnidades'
import PsetsDoType from './PsetsDoType'
import { getInheritance } from '@viewer/lib/ifcInheritance'
import CoordinatesView from './CoordinatesView'
import RawRepresentationView from './RawRepresentationView'
import { textoDaQuantidadeEscrita } from '@modules/boq/lib/unidadesBoq'
import {
  EDITABLE_ATTRIBUTES,
  getEditableAttributeChoices,
  ifcEditApi,
  isAttributeEditableForCategory,
} from '@modules/ifc-edit/api'
import {
  getCompatibleClassesIncludingSelf,
  isClassChangeable,
} from '@modules/ifc-edit/lib/classChange'
import { getPredefinedTypeInfo } from '@modules/ifc-edit/lib/predefinedType'
import { useAuth } from '@core/auth/useAuth'
import Section from './Section'
import DocumentosDoElemento from './DocumentosDoElemento'
import KeyValueTable from './KeyValueTable'
import MultiSelectionView from './MultiSelectionView'
import ModeloDeOrigem from './ModeloDeOrigem'
import { useNomesDosModelos } from '@viewer/lib/useNomeDoModelo'
import { filterProps } from './propertyFilter'
import { juntarComLeituraEmBruto, textoDaAreaCalculada, textoDaCotaDoMotor, type CotaDoMotor } from './juntarDados'

// Pull the original (Fragments-derived) value for a top-level
// attribute. Used by the inline editor to collapse the overlay back
// to "no edit" when the user types the original value.
function baselineAttribute(
  data: Record<string, unknown>,
  key: string,
): string | null {
  const v = data[key]
  if (v === null || v === undefined) return null
  if (typeof v === 'string') return v
  if (typeof v === 'object' && 'value' in (v as object)) {
    const inner = (v as { value: unknown }).value
    if (inner === null || inner === undefined) return null
    return String(inner)
  }
  return String(v)
}

// Apply the IFCEdit overlay on top of a Fragments-derived header value
// so the truncated chips in the panel header stay in sync with edits
// the user makes inline. Returns `'-'` for null/empty (matches what
// formatValue produces for missing values).
function overlayHeader(
  base: string,
  overlay: Record<string, string | null> | undefined,
  key: string,
): string {
  if (!overlay || !(key in overlay)) return base
  const v = overlay[key]
  if (v === null || v === '') return '-'
  return v
}

interface PropertiesPanelProps {
  // When provided, the panel ignores the global selection and shows
  // this fixed target (used by floating popups). History/back/forward
  // are hidden in pinned mode.
  pinnedSelection?: { modelId: string; localId: number } | null
}


function PropertiesPanel({ pinnedSelection }: PropertiesPanelProps = {}) {
  const globalSelection = useAppStore((s) => s.selection)
  const selectedTargets = useAppStore((s) => s.selectedTargets)
  const clearSelection = useAppStore((s) => s.clearSelection)
  // BoQ-written psets are read live from the BoQ session every render
  // (the writeLog lives in the session, not the store). We just
  // subscribe to a version counter so this re-renders when the log
  // changes: the actual lookup is `session.getWrittenPsets(guid)`.
  const boqSession = useAppStore((s) => s.boqSession)
  const _boqWriteVersion = useAppStore((s) => s.boqWriteVersion)

  // IFCEdit lives entirely client-side: edits are stored in an
  // overlay slice and applied visually here. The viewer's Fragments
  // data doesn't see them (web-ifc-side persistence lands later).
  const ifcEditAttributeEdits = useAppStore((s) => s.ifcEditAttributeEdits)
  const ifcEditClassChanges = useAppStore((s) => s.ifcEditClassChanges)
  const ifcEditPsetEdits = useAppStore((s) => s.ifcEditPsetEdits)
  const _ifcEditVersion = useAppStore((s) => s.ifcEditVersion)
  const setIfcEditAttribute = useAppStore((s) => s.setIfcEditAttribute)
  const clearIfcEditAttribute = useAppStore((s) => s.clearIfcEditAttribute)
  const setIfcEditClass = useAppStore((s) => s.setIfcEditClass)
  const clearIfcEditClass = useAppStore((s) => s.clearIfcEditClass)
  const setIfcEditPset = useAppStore((s) => s.setIfcEditPset)
  const clearIfcEditPset = useAppStore((s) => s.clearIfcEditPset)
  const auth = useAuth()
  const ifcEditEnabled =
    auth.mode === 'cloud' && auth.modules.includes('ifc-edit')
  const selection = pinnedSelection ?? globalSelection

  // A unidade de cada propriedade: a que declara, ou a do projecto.
  const unidadeDe = useUnidadeDe(selection?.modelId ?? null)
  const isPinned = !!pinnedSelection
  const showSummary = !isPinned && selectedTargets.length > 1
  // Schema of the selected element's model, needed by IFCEdit to
  // populate enum dropdowns (PredefinedType values differ between
  // IFC2X3, IFC4 and IFC4X3, and some classes only got
  // PredefinedType in IFC4+). Null when the loader didn't manage
  // to read FILE_SCHEMA; in that case we degrade to free-text.
  const modelSchema = useAppStore((s) => {
    if (!selection) return null
    const m = s.models.find((m) => m.id === selection.modelId)
    return m?.schemaVersion ?? null
  })
  const history = useAppStore((s) => s.selectionHistory)
  const historyIndex = useAppStore((s) => s.historyIndex)
  const goBackSelection = useAppStore((s) => s.goBackSelection)
  const goForwardSelection = useAppStore((s) => s.goForwardSelection)
  const openPopup = useAppStore((s) => s.openPopup)
  const propsAdvanced = useAppStore((s) => s.propsAdvanced)
  const setPropsAdvanced = useAppStore((s) => s.setPropsAdvanced)
  // Com vários modelos, a navegação diz de que modelo é cada elemento.
  const { nomeDe: nomeDoModeloDe, varios: variosModelos } = useNomesDosModelos()
  const modeloDoHistorico = (i: number): string => {
    const alvo = history[i]
    const n = alvo && variosModelos ? nomeDoModeloDe(alvo.modelId) : null
    return n ? ` (${n.minimo})` : ''
  }
  const canGoBack = !isPinned && historyIndex > 0
  const canGoForward =
    !isPinned && historyIndex >= 0 && historyIndex < history.length - 1
  const onBack = () => {
    const target = goBackSelection()
    if (target) {
      void viewerApi.selectByLocalId(target.modelId, target.localId)
    }
  }
  const onForward = () => {
    const target = goForwardSelection()
    if (target) {
      void viewerApi.selectByLocalId(target.modelId, target.localId)
    }
  }
  const [data, setData] = useState<Record<string, unknown> | null>(null)
  // O valor que o motor guarda para a Elevation/RefElevation, quando o
  // painel mostra o do ficheiro (ou o aviso, quando não há ficheiro).
  const [cotaDoMotor, setCotaDoMotor] = useState<CotaDoMotor | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showRaw, setShowRaw] = useState(false)
  const [coords, setCoords] = useState<CoordinateInfo | null>(null)
  const [ancestorMeta, setAncestorMeta] = useState<
    Map<number, { category: string; name: string }>
  >(new Map())
  const [modelInfo, setModelInfo] = useState<ModelInfo | null>(null)
  const [showGlobal, setShowGlobal] = useState(false)
  const [rawInfo, setRawInfo] = useState<RawElementInfo | null>(null)
  // Pesquisa dentro das propriedades (atributos, psets, qsets). Persiste
  // enquanto a seleção muda (o painel não é remontado por seleção).
  const [propSearch, setPropSearch] = useState('')

  useEffect(() => {
    if (!selection || showSummary) {
      // Summary mode (>1 selected) renders counts only; skipping the
      // heavy getItemsData call keeps shift-click ranges responsive on
      // large models where each fetch can take seconds.
      setData(null)
      setError(null)
      setLoading(false)
      return
    }
    const model = viewerApi.getModel(selection.modelId)
    if (!model) {
      setError(`Modelo ${selection.modelId} não está registado.`)
      setData(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setCotaDoMotor(null)
    setError(null)
    model
      .getItemsData([selection.localId], {
        attributesDefault: true,
        // Pull every IFC inverse relation present on this entity, with
        // one level of attribute expansion so the UI can show category
        // and Name on each target. Going deeper (relations: true) blows
        // up payload size on highly connected entities (e.g. an
        // IfcBuildingStorey returning thousands of contained elements).
        relationsDefault: { attributes: true, relations: false },
        // Exception: IsDefinedBy carries the property/quantity sets, and
        // their values live one level deeper (IfcPropertySet.HasProperties
        // -> IfcPropertySingleValue.NominalValue, IfcElementQuantity
        // .Quantities -> *Value). Without deepening this single relation
        // every pset comes back name-only, extractPsetRows yields zero
        // rows, and the panel shows "Sem property sets" for every element.
        // Deepening ONLY IsDefinedBy keeps the storey-blowup guard intact
        // (ContainsElements/Decomposes stay shallow via relationsDefault).
        relations: { IsDefinedBy: { attributes: true, relations: true } },
      })
      .then(async (result) => {
        if (cancelled) return
        const first = result[0]
        // A Elevation do piso e a RefElevation do site vêm do ficheiro
        // (ver juntarDados.ts): se a leitura em bruto ainda não estiver
        // carregada, carrega-se agora para estas duas classes.
        const cat = first && typeof first === 'object' ? getCategory(first as Record<string, unknown>).toUpperCase() : ''
        if (
          (cat === 'IFCBUILDINGSTOREY' || cat === 'IFCSITE') &&
          !rawIfc.hasModel(selection.modelId) &&
          rawIfc.hasCachedBytes(selection.modelId)
        ) {
          try {
            await rawIfc.loadModel(selection.modelId)
          } catch {
            /* sem leitura em bruto: fica o valor do motor, assinalado */
          }
          if (cancelled) return
        }
        // Fragments indexes geometric items, so non-geometric entities
        // (IfcSystem, IfcGroup, IfcZone, IfcDistributionSystem,
        // IfcElementAssembly, ...) come back as a near-empty payload.
        // Merge in whatever the parallel rawIfc parser knows for the
        // same expressID, with Fragments winning when both have a key.
        // This keeps Atributos / PredefinedType / LongName editing
        // available across the full entity catalogue.
        let merged: Record<string, unknown> | null =
          first && typeof first === 'object'
            ? (first as Record<string, unknown>)
            : null
        let cota: CotaDoMotor | null = null
        try {
          const raw = rawIfc.getEntity(
            selection.modelId,
            selection.localId,
          ) as Record<string, unknown> | null
          if (raw && typeof raw === 'object') {
            const junto = juntarComLeituraEmBruto(merged, raw, cat)
            const out = junto.dados
            cota = junto.cotaDoMotor
            // Ensure category is set: rawIfc.type is the IFC class
            // name (e.g. "IFCDISTRIBUTIONSYSTEM"). Fragments uses
            // `_category` lowercased; expose under both keys so the
            // downstream `getCategory` helper resolves it either way.
            if (
              !out._category &&
              typeof raw.type === 'string' &&
              raw.type
            ) {
              out._category = raw.type
            }
            merged = out
          }
        } catch {
          /* rawIfc may not have this entity: keep Fragments-only. */
        }
        // Sem o ficheiro (um .frag), o valor que fica é o do motor: diz-se.
        if (!cota && merged) {
          const atributo = cat === 'IFCBUILDINGSTOREY' ? 'Elevation' : cat === 'IFCSITE' ? 'RefElevation' : null
          if (atributo && merged[atributo] != null && !rawIfc.hasModel(selection.modelId)) {
            cota = { atributo, valor: merged[atributo], semFicheiro: true }
          }
        }
        setCotaDoMotor(cota)
        if (merged) {
          console.debug(
            '[PropertiesPanel] item keys',
            Object.keys(merged),
            { sample: merged },
          )
          setData(merged)
        } else {
          setData(null)
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selection, showSummary])

  // Per-selection coordinate fetch: world position + bbox + ancestor
  // positions in one shot. Independent from the attribute fetch above
  // because it uses different model methods (getPositions / getBoxes).
  useEffect(() => {
    if (!selection || showSummary) {
      setCoords(null)
      setAncestorMeta(new Map())
      return
    }
    const model = viewerApi.getModel(selection.modelId)
    if (!model) return
    let cancelled = false
    const ancestors = viewerApi.getAncestorPath(
      selection.modelId,
      selection.localId,
    )
    void Promise.all([
      loadCoordinateInfo(
        model,
        selection.localId,
        ancestors,
        (lid) => viewerApi.getDescendants(selection.modelId, lid),
      ),
      ancestors.length > 0
        ? model.getItemsData(ancestors, {
            attributesDefault: true,
            relationsDefault: { attributes: false, relations: false },
          })
        : Promise.resolve([]),
    ])
      .then(([coordInfo, ancestorData]) => {
        if (cancelled) return
        setCoords(coordInfo)
        const map = new Map<number, { category: string; name: string }>()
        ancestorData.forEach((item, idx) => {
          if (!item || typeof item !== 'object') return
          const o = item as Record<string, unknown>
          let cat = ''
          const rawCat = o._category
          if (typeof rawCat === 'string') cat = rawCat
          else if (
            rawCat &&
            typeof rawCat === 'object' &&
            'value' in (rawCat as object)
          ) {
            cat = String((rawCat as { value: unknown }).value ?? '')
          }
          const nameAttr = o.Name as { value?: unknown } | undefined
          const name =
            nameAttr && typeof nameAttr === 'object' && 'value' in nameAttr
              ? String(nameAttr.value ?? '')
              : ''
          map.set(ancestors[idx], { category: cat, name })
        })
        setAncestorMeta(map)
      })
      .catch((err) => console.warn('[PropertiesPanel] coords failed', err))
    return () => {
      cancelled = true
    }
  }, [selection, showSummary])

  // Pull the raw IFC entity directly from the parallel web-ifc parser
  // (held by rawIfc). This is what the Fragments conversion drops:
  // IfcLocalPlacement chain and IfcShapeRepresentation items.
  useEffect(() => {
    if (!selection || showSummary) {
      setRawInfo(null)
      return
    }
    if (!rawIfc.hasModel(selection.modelId)) {
      setRawInfo(null)
      // Subscribe in case the parallel parser is still loading.
      const unsub = rawIfc.subscribe(() => {
        if (rawIfc.hasModel(selection.modelId)) {
          unsub()
          fetchRaw()
        }
      })
      return unsub
    }
    fetchRaw()
    return undefined

    function fetchRaw() {
      if (!selection) return
      try {
        const entity = rawIfc.getEntity(selection.modelId, selection.localId)
        const chain = rawIfc.getPlacementChain(
          selection.modelId,
          selection.localId,
        )
        setRawInfo(decodeRawElement(entity, chain))
      } catch (err) {
        console.warn('[PropertiesPanel] rawIfc fetch failed', err)
        setRawInfo(null)
      }
    }
  }, [selection, showSummary])

  // Load model-level info (CRS) once per model, needed to offer the
  // local→global conversion button.
  useEffect(() => {
    if (!selection || showSummary) return
    const model = viewerApi.getModel(selection.modelId)
    if (!model) return
    let cancelled = false
    loadModelInfo(model)
      .then((info) => {
        if (!cancelled) setModelInfo(info)
      })
      .catch(() => {
        /* ignore */
      })
    return () => {
      cancelled = true
    }
  }, [selection, showSummary])

  const view = useMemo(() => {
    if (!data) return null
    let category = getCategory(data)
    const guid = findGuid(data)
    // The header chips below need to reflect IFCEdit overlay edits or
    // the inline editor feels broken (typed value disappears once the
    // input loses focus, the chip still shows the original). Apply
    // overlay BEFORE the rest of the view derives from these values.
    const overlay = guid ? ifcEditAttributeEdits[guid] : undefined
    // Same idea for class changes: the chip at the top should reflect
    // the user's pending class change, not the original.
    const baseCategory = category
    const pendingClass = guid ? ifcEditClassChanges[guid] : undefined
    if (pendingClass) {
      // pendingClass is canonical camel-case (e.g. "IfcSlab"); leave it
      // as-is so the header reads cleanly. STEP patcher uppercases on
      // export.
      category = pendingClass
    }
    let name = overlayHeader(formatValue(data.Name), overlay, 'Name')
    const description = overlayHeader(
      formatValue(data.Description),
      overlay,
      'Description',
    )
    const tag = overlayHeader(formatValue(data.Tag), overlay, 'Tag')
    const objectType = overlayHeader(
      formatValue(data.ObjectType),
      overlay,
      'ObjectType',
    )
    const longName = overlayHeader(
      formatValue(data.LongName),
      overlay,
      'LongName',
    )
    // Fragments returns near-empty payloads for IfcSystem / IfcGroup /
    // IfcZone / IfcAssembly (it indexes geometric items only). For
    // those non-geometric entities we fall back to web-ifc so the
    // popup at least shows the entity name and class instead of
    // "(sem nome)".
    if (selection && (!category || name === '-')) {
      try {
        const raw = rawIfc.getEntity(
          selection.modelId,
          selection.localId,
        ) as { type?: unknown; Name?: { value?: unknown } } | null
        if (raw) {
          if (!category && typeof raw.type === 'string') category = raw.type
          if (name === '-') {
            const v = raw.Name && typeof raw.Name === 'object'
              ? (raw.Name as { value?: unknown }).value
              : null
            if (typeof v === 'string' && v) name = v
          }
        }
      } catch {
        /* ignore, keep Fragments-side defaults */
      }
    }
    // Detect group/system-like categories so we can render a
    // "Selecionar membros" action and skip noisy attribute sections
    // that always come back empty for them.
    const upperCat = (category || '').toUpperCase()
    const isGroupLike =
      upperCat.includes('SYSTEM') ||
      upperCat === 'IFCGROUP' ||
      upperCat === 'IFCZONE' ||
      upperCat === 'IFCELEMENTASSEMBLY' ||
      upperCat === 'IFCSPATIALZONE' ||
      upperCat === 'IFCSTRUCTURALANALYSISMODEL'
    const memberCount = isGroupLike && selection
      ? rawIfc.getGroupMembers(selection.modelId, selection.localId).length
      : 0
    const baseAttributes = getAttributes(data)
    // IFCEdit overlay: if the user has typed values into editable
    // attributes (Name/Description/Tag/ObjectType/LongName), show
    // them in-place. The overlay already drove the header chips
    // above; re-apply to the Atributos rows so the inline editor's
    // current value matches what the user typed.
    const overlaidAttributes: KeyValue[] = baseAttributes.map((row) => {
      if (overlay && row.key in overlay) {
        const v = overlay[row.key]
        return { ...row, value: v === null ? '' : v }
      }
      return row
    })
    // Synthesize empty rows for editable attributes that apply to this
    // class but aren't populated in the Fragments payload, e.g. an
    // IfcSpace without LongName, or an IfcWall without Tag. Without
    // these the user has no surface to add a missing value; with them
    // they get a "vazio" row with the pencil affordance.
    const presentKeys = new Set(overlaidAttributes.map((r) => r.key))
    const synthesizedAttributes: KeyValue[] = []
    for (const attr of EDITABLE_ATTRIBUTES) {
      if (presentKeys.has(attr)) continue
      if (!isAttributeEditableForCategory(attr, category, modelSchema)) continue
      const overlaid = overlay && attr in overlay
      const v = overlaid ? overlay![attr] : null
      synthesizedAttributes.push({
        key: attr,
        value: v === null || v === undefined ? '' : v,
      })
    }
    // Class change is offered as a synthesised "Classe" row when the
    // base category is in the IfcBuildingElement compatibility set
    // (see classChange.ts). Shown even when there's a pending change
    // so the user can flip back or onwards. The current value is the
    // overlay if present, otherwise the base category.
    const classRowApplies = isClassChangeable(baseCategory)
    if (classRowApplies) {
      synthesizedAttributes.push({
        key: 'Classe',
        value: category,
      })
    }
    const attributes: KeyValue[] = [
      ...overlaidAttributes,
      ...synthesizedAttributes,
    ].sort((a, b) => a.key.localeCompare(b.key))
    const { psets, qsets } = getPsetsAndQsets(data)
    // Apply pset edit overlay to displayed values. Overlay keys are
    // psetName → propName; nested ComplexProperty keys (which contain
    // a `.`) are left alone: the patcher doesn't navigate them and
    // the inline editor refuses to expose them either.
    const psetOverlayForGuid =
      (guid ? ifcEditPsetEdits[guid] : undefined) ?? {}
    const overlayPsetRows = (groups: PsetGroup[]): PsetGroup[] =>
      groups.map((g) => {
        const o = psetOverlayForGuid[g.name]
        if (!o) return g
        return {
          ...g,
          rows: g.rows.map((r) => {
            if (r.key.includes('.')) return r
            if (r.key in o) {
              const v = o[r.key]
              return { ...r, value: v === null ? '' : v }
            }
            return r
          }),
        }
      })
    const psetsOverlaid = overlayPsetRows(psets)
    const qsetsOverlaid = overlayPsetRows(qsets)
    const psetsNonEmpty = psetsOverlaid.filter((g) => g.rows.length > 0)
    const qsetsNonEmpty = qsetsOverlaid.filter((g) => g.rows.length > 0)

    // Splice in BoQ-written psets/qsets straight into the existing
    // Property sets / Quantity sets buckets. Routing is by name
    // prefix so the user sees them under the IFC schema section
    // they belong to, not in a separate "BoQ" silo:
    //   Qto_*  (canonical buildingSMART, e.g. Qto_WallBaseQuantities)
    //          → Quantity sets
    //   anything else (including VN2R_*BaseQuantities)
    //          → Property sets
    // Each spliced entry carries `viaBoQ: true` so the renderer can
    // tag it with a subtle badge: the user wants to know the
    // provenance without the BoQ entries dominating the panel.
    const boqEntries =
      boqSession && guid ? boqSession.getWrittenPsets(guid) : []
    const latestByName = new Map<string, (typeof boqEntries)[number]>()
    for (const e of boqEntries) latestByName.set(e.psetName, e)
    const existingNames = new Set([
      ...psetsNonEmpty.map((g) => g.name),
      ...qsetsNonEmpty.map((g) => g.name),
    ])
    // Cada quantidade leva a unidade em que o BoQ a escreveu (a do
    // projecto, ou a que criou com confirmação) e a marca de calculado.
    // Antes deduzia-se do comprimento, com "m" e "kg" por omissão.
    const boqGroups: PsetGroup[] = []
    for (const e of latestByName.values()) {
      if (existingNames.has(e.psetName)) continue
      const rows: KeyValue[] = Object.entries(e.quantities).map(([k, v]) => ({
        key: k,
        value:
          typeof v === 'number'
            ? textoDaQuantidadeEscrita(v, e.unidades?.[k] ?? 'unidade não declarada')
            : String(v),
      }))
      boqGroups.push({
        name: e.psetName,
        ifcClass: 'IfcElementQuantity',
        rows,
        viaBoQ: true,
      })
    }
    const psetsWithBoq = [
      ...psetsNonEmpty,
      ...boqGroups.filter((g) => !g.name.startsWith('Qto_')),
    ]
    const qsetsWithBoq = [
      ...qsetsNonEmpty,
      ...boqGroups.filter((g) => g.name.startsWith('Qto_')),
    ]

    const { materials, classifications } = getMaterialsAndClassifications(data)
    const location = getLocation(data)
    const inverseRelations = getInverseRelations(data)
    // Fragments only surfaces a fixed set of inverse relations on
    // getItemsData (HasAssignments is not always populated even when
    // the IFC has IfcRelAssignsToGroup). Fold in the group/system
    // memberships we computed from the parallel rawIfc parser so the
    // user sees IfcSystem/IfcGroup links here without having to dig
    // into the "Sistemas, grupos e assemblies" panel.
    if (selection) {
      const memberships = rawIfc.getGroupsOfElement(
        selection.modelId,
        selection.localId,
      )
      if (memberships.length > 0) {
        const existing = inverseRelations.find((r) => r.name === 'HasAssignments')
        const targets = memberships.map((m) => ({
          category: m.category || 'IfcGroup',
          name: m.name ?? '(sem nome)',
          guid: null,
          localId: m.expressID,
        }))
        if (existing) {
          // Avoid duplicating entries Fragments already returned
          // (matched by localId).
          const seen = new Set(
            existing.targets
              .map((t) => t.localId)
              .filter((id): id is number => id != null),
          )
          for (const t of targets) {
            if (!seen.has(t.localId)) existing.targets.push(t)
          }
        } else {
          inverseRelations.push({ name: 'HasAssignments', targets })
          inverseRelations.sort((a, b) => a.name.localeCompare(b.name))
        }
      }
    }

    // IfcRelSpaceBoundary inverse: Fragments doesn't populate
    // IfcSpace.BoundedBy nor IfcElement.ProvidesBoundaries. We scan
    // the IFC once via the parallel parser and fold both directions
    // into the inverse-relations section so an IfcSpace surfaces its
    // bounding walls/slabs and a wall surfaces the spaces it
    // bounds. Annotation in the target Name carries the boundary
    // classification (PHYSICAL/VIRTUAL, INTERNAL/EXTERNAL) plus the
    // 2nd-level surface area when we can compute it, useful for
    // envelope + thermal analysis ("quanto da parede X dá para o
    // espaço Y").
    if (selection) {
      const isSpace = upperCat === 'IFCSPACE' || upperCat === 'IFCSPATIALZONE'
      const boundaries = isSpace
        ? rawIfc.getBoundariesOfSpace(selection.modelId, selection.localId)
        : rawIfc.getBoundariesOfElement(selection.modelId, selection.localId)
      if (boundaries.length > 0) {
        // A área é calculada da geometria da ligação, na unidade de
        // comprimento do ficheiro ao quadrado: diz-se, e sem unidade
        // declarada não se assume o metro.
        const lengthUnit = rawIfc.getLengthUnit(selection.modelId)?.label ?? null
        const targets = boundaries.map((b) => {
          const otherSideId = isSpace ? b.elementExpressID : b.spaceExpressID
          let category = ''
          let name: string | null = null
          let guid: string | null = null
          if (otherSideId != null) {
            const summary = rawIfc.getEntitySummary(
              selection.modelId,
              otherSideId,
            )
            if (summary) {
              category = summary.category
              name = summary.name
              guid = summary.guid
            }
          }
          const tags: string[] = []
          if (b.physicalOrVirtual) tags.push(b.physicalOrVirtual)
          if (b.internalOrExternal) tags.push(b.internalOrExternal)
          if (otherSideId == null) tags.push('sem elemento')
          if (b.area != null && b.area > 0) {
            tags.push(textoDaAreaCalculada(b.area, lengthUnit))
          }
          const baseName = name ?? '(sem nome)'
          const displayName =
            tags.length > 0 ? `${baseName} [${tags.join(', ')}]` : baseName
          return {
            category:
              category ||
              (otherSideId == null ? 'IfcVirtualElement' : 'IfcElement'),
            name: displayName,
            guid,
            localId: otherSideId,
          }
        })
        const relName = isSpace ? 'BoundedBy' : 'ProvidesBoundaries'
        const existing = inverseRelations.find((r) => r.name === relName)
        if (existing) {
          const seen = new Set(
            existing.targets
              .map((t) => t.localId)
              .filter((id): id is number => id != null),
          )
          for (const t of targets) {
            if (t.localId == null || !seen.has(t.localId)) {
              existing.targets.push(t)
            }
          }
        } else {
          inverseRelations.push({ name: relName, targets })
          inverseRelations.sort((a, b) => a.name.localeCompare(b.name))
        }
      }
    }

    // Generic inverse-relation fold: scans every IFC rel type the
    // parallel parser knows about (Covers, Projects, Interferes,
    // Nests, Declares, Sequence, ServicesBuildings, Connects,
    // Fills, Voids, Contains, References) and folds each entry
    // under the canonical IFC inverse-attribute name. Dedupe is by
    // (relName, localId), so an entry Fragments already gave us
    // (e.g. Decomposes from IfcRelAggregates) is silently swallowed
    // and the user sees a single, unified list. Empty groups never
    // get added.
    if (selection) {
      const extras = rawIfc.getExtraInverseRelations(
        selection.modelId,
        selection.localId,
      )
      if (extras.length > 0) {
        const byName = new Map<
          string,
          Array<{ inverseName: string; targetExpressID: number }>
        >()
        for (const e of extras) {
          let bucket = byName.get(e.inverseName)
          if (!bucket) {
            bucket = []
            byName.set(e.inverseName, bucket)
          }
          bucket.push(e)
        }
        for (const [name, entries] of byName) {
          const newTargets = entries.map((e) => {
            const summary = rawIfc.getEntitySummary(
              selection.modelId,
              e.targetExpressID,
            )
            return {
              category: summary?.category || '?',
              name: summary?.name ?? '(sem nome)',
              guid: summary?.guid ?? null,
              localId: e.targetExpressID as number | null,
            }
          })
          const existing = inverseRelations.find((r) => r.name === name)
          if (existing) {
            const seen = new Set(
              existing.targets
                .map((t) => t.localId)
                .filter((id): id is number => id != null),
            )
            for (const t of newTargets) {
              if (t.localId == null || !seen.has(t.localId)) {
                existing.targets.push(t)
              }
            }
          } else {
            inverseRelations.push({ name, targets: newTargets })
          }
        }
        inverseRelations.sort((a, b) => a.name.localeCompare(b.name))
      }
    }
    const inheritance = getInheritance(category)
    const typeInfo = getType(data)
    const geometry = getGeometry(data)
    return {
      category,
      baseCategory,
      name,
      guid,
      description,
      tag,
      objectType,
      longName,
      attributes,
      psets: psetsWithBoq,
      qsets: qsetsWithBoq,
      materials,
      classifications,
      location,
      inverseRelations,
      inheritance,
      typeInfo,
      geometry,
      isGroupLike,
      memberCount,
    }
    // boqWriteVersion is intentionally a dependency so a write/undo
    // re-runs the lookup. boqSession identity also changes when the
    // user troca IFC. modelSchema flips between models so the
    // synthesized-row set + PredefinedType choices stay aligned.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    boqSession,
    _boqWriteVersion,
    ifcEditAttributeEdits,
    ifcEditClassChanges,
    ifcEditPsetEdits,
    _ifcEditVersion,
    modelSchema,
  ])

  const searching = propSearch.trim() !== ''
  const filtered = useMemo(
    () =>
      filterProps(
        view ?? { attributes: [], psets: [], qsets: [] },
        propSearch,
      ),
    [view, propSearch],
  )

  if (!selection) {
    return (
      <IfcxElementoView
        semNada={
          <div className="p-4 text-sm text-[var(--color-text-muted)]">
            Clica num elemento no viewer para ver as suas propriedades.
          </div>
        }
      />
    )
  }

  return (
    <div className="flex h-full flex-col">
      {view && !showSummary && (
        <div className="border-b border-[var(--color-border)] px-3 py-2">
          {!isPinned && (
            <div className="mb-1 flex items-center gap-1">
              <button
                type="button"
                onClick={onBack}
                disabled={!canGoBack}
                title={
                  canGoBack && historyIndex > 0
                    ? `Voltar a #${history[historyIndex - 1]?.localId ?? ''}${modeloDoHistorico(historyIndex - 1)}`
                    : 'Sem histórico anterior'
                }
                className="rounded border border-[var(--color-border)] px-1.5 py-0 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:cursor-not-allowed disabled:opacity-30"
              >
                ←
              </button>
              <button
                type="button"
                onClick={onForward}
                disabled={!canGoForward}
                title={
                  canGoForward
                    ? `Avançar a #${history[historyIndex + 1]?.localId ?? ''}${modeloDoHistorico(historyIndex + 1)}`
                    : 'Sem histórico em frente'
                }
                className="rounded border border-[var(--color-border)] px-1.5 py-0 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:cursor-not-allowed disabled:opacity-30"
              >
                →
              </button>
              {history.length > 1 && (
                <span className="ml-1 text-[10px] text-[var(--color-text-dim)]">
                  {historyIndex + 1} / {history.length}
                </span>
              )}
            </div>
          )}
          <div className="text-[10px] uppercase tracking-wide text-[var(--color-accent)]">
            {view.category || 'Elemento'}
          </div>
          <div className="truncate text-sm font-medium text-[var(--color-text)]">
            {view.name !== '-' ? view.name : '(sem nome)'}
          </div>
          {selection && <ModeloDeOrigem modelId={selection.modelId} />}
          {view.isGroupLike && selection && (
            <button
              type="button"
              onClick={(ev) => {
                const ids = rawIfc.getGroupMembers(
                  selection.modelId,
                  selection.localId,
                )
                if (ids.length === 0) return
                const targets = ids.map((id) => ({
                  modelId: selection.modelId,
                  localId: id,
                }))
                useAppStore
                  .getState()
                  .applyBulkSelection(
                    targets,
                    ev.shiftKey || ev.ctrlKey || ev.metaKey ? 'add' : 'replace',
                  )
              }}
              disabled={view.memberCount === 0}
              className="mt-1 rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-0.5 text-[11px] text-[var(--color-accent)] hover:bg-[var(--color-accent)]/20 disabled:opacity-40"
              title="Selecionar todos os elementos pertencentes a este sistema/grupo (Shift/Ctrl: somar à selecção atual)"
            >
              Selecionar {view.memberCount} membro(s)
            </button>
          )}
          <div className="mt-2 grid grid-cols-[auto_1fr_auto] items-center gap-x-2 gap-y-1 text-[10px]">
            <span className="text-[var(--color-text-muted)]">GlobalId</span>
            <code className="truncate rounded bg-[var(--color-bg)]/60 px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text)]">
              {view.guid ?? 'null'}
            </code>
            <button
              type="button"
              disabled={view.guid === null}
              onClick={() => {
                if (view.guid) void navigator.clipboard?.writeText(view.guid)
              }}
              className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
            >
              Copiar
            </button>

            <span className="text-[var(--color-text-muted)]">Local ID</span>
            <code className="truncate rounded bg-[var(--color-bg)]/60 px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text)]">
              {selection.localId}
            </code>
            <span />

            {view.tag !== '-' && (
              <>
                <span className="text-[var(--color-text-muted)]">Tag</span>
                <code className="truncate rounded bg-[var(--color-bg)]/60 px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text)]">
                  {view.tag}
                </code>
                <span />
              </>
            )}
            {view.objectType !== '-' && (
              <>
                <span className="text-[var(--color-text-muted)]">
                  ObjectType
                </span>
                <code className="truncate rounded bg-[var(--color-bg)]/60 px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text)]">
                  {view.objectType}
                </code>
                <span />
              </>
            )}
            {view.longName !== '-' && (
              <>
                <span className="text-[var(--color-text-muted)]">
                  LongName
                </span>
                <code className="truncate rounded bg-[var(--color-bg)]/60 px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text)]">
                  {view.longName}
                </code>
                <span />
              </>
            )}
          </div>
        </div>
      )}

      {showSummary && (
        <MultiSelectionSummary
          targets={selectedTargets}
          onClear={clearSelection}
        />
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        {!showSummary && loading && (
          <div className="p-4 text-xs text-[var(--color-text-muted)]">
            A carregar propriedades…
          </div>
        )}
        {!showSummary && error && (
          <div className="m-3 rounded-md border border-[var(--color-error-border)] bg-[var(--color-error-bg)] p-2 text-xs text-[var(--color-error-text)]">
            {error}
          </div>
        )}
        {showSummary && (
          <MultiSelectionView targets={selectedTargets} />
        )}
        {!loading && !error && view && !showSummary && (
          <>
            <div className="sticky top-0 z-10 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={propSearch}
                  onChange={(e) => setPropSearch(e.target.value)}
                  placeholder="Procurar propriedade ou valor…"
                  className="min-w-0 flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-[11px] text-[var(--color-text)] focus:border-[var(--color-accent)] focus:outline-none"
                  title="Filtra os atributos, property sets e quantity sets por nome de set, nome de propriedade ou valor. O termo mantém-se ao trocar de elemento."
                />
                <label
                  className="flex shrink-0 cursor-pointer items-center gap-1 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  title="Mostrar também relações inversas, cadeia de herança, tipo IFC e geometria (representação)."
                >
                  <input
                    type="checkbox"
                    checked={propsAdvanced}
                    onChange={(e) => setPropsAdvanced(e.target.checked)}
                    className="accent-[var(--color-accent)]"
                  />
                  Avançado
                </label>
                {propsAdvanced && selection && (
                  <button
                    type="button"
                    onClick={() => useAppStore.getState().abrirNaEstrutura(selection.modelId, selection.localId)}
                    className="shrink-0 rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                    title="Abrir este elemento no painel Estrutura IFC: todos os atributos pelo esquema, as referências, quem o referencia e a linha STEP."
                  >
                    Estrutura IFC
                  </button>
                )}
              </div>
              {searching && (
                <div className="mt-1 flex items-center gap-2 text-[10px] text-[var(--color-text-dim)]">
                  <span>
                    A filtrar atributos, psets e qsets por “{propSearch.trim()}”.
                  </span>
                  <button
                    type="button"
                    onClick={() => setPropSearch('')}
                    className="rounded border border-[var(--color-border)] px-1 py-px hover:text-[var(--color-text)]"
                  >
                    limpar
                  </button>
                </div>
              )}
            </div>
            {/* Atributos fica na vista SIMPLES: Name, Tag, ObjectType,
                LongName e a classe sao informacao basica e de uso diario,
                ao nivel dos property sets. */}
            <Section
              title="Atributos"
              count={filtered.attributes.length}
              defaultOpen
              forceOpen={searching}
            >
              <KeyValueTable
                rows={filtered.attributes}
                emptyMessage={
                  searching ? 'Nenhum atributo corresponde.' : undefined
                }
                getChoices={
                  ifcEditEnabled
                    ? (key) => {
                        if (key === 'Classe') {
                          return getCompatibleClassesIncludingSelf(
                            view.baseCategory,
                          )
                        }
                        return getEditableAttributeChoices(
                          key,
                          view.category,
                          modelSchema,
                        )
                      }
                    : undefined
                }
                getOnSave={
                  ifcEditEnabled && view.guid && data
                    ? (key) => {
                        const guidNonNull = view.guid!
                        // Class change has its own overlay + special
                        // post-process (auto-fallback PredefinedType
                        // when the existing value isn't valid for the
                        // new class's enum).
                        if (key === 'Classe') {
                          if (!isClassChangeable(view.baseCategory)) {
                            return null
                          }
                          return async (next: string) => {
                            const target = next || view.baseCategory
                            if (target === view.baseCategory) {
                              clearIfcEditClass(guidNonNull)
                            } else {
                              setIfcEditClass(guidNonNull, target)
                              // Auto-fallback PredefinedType if the
                              // existing value isn't a member of the
                              // new class's enum on this schema.
                              const overlay = ifcEditAttributeEdits[guidNonNull]
                              const overlayHas =
                                overlay && 'PredefinedType' in overlay
                              const currentPredef = (
                                overlayHas
                                  ? overlay!.PredefinedType
                                  : data
                                    ? formatValue(data.PredefinedType)
                                    : ''
                              )
                                ?.toString()
                                .toUpperCase() ?? ''
                              const info = getPredefinedTypeInfo(
                                target,
                                modelSchema,
                              )
                              if (
                                info &&
                                currentPredef &&
                                currentPredef !== '-' &&
                                !info.choices.includes(currentPredef)
                              ) {
                                setIfcEditAttribute(
                                  guidNonNull,
                                  'PredefinedType',
                                  'NOTDEFINED',
                                )
                              }
                            }
                            ifcEditApi.audit('ifc_edit.attribute.edit', {
                              guid: guidNonNull,
                              attribute: 'Classe',
                            })
                          }
                        }
                        if (
                          !isAttributeEditableForCategory(
                            key,
                            view.category,
                            modelSchema,
                          )
                        ) {
                          return null
                        }
                        // Original (Fragments-derived) baseline, used to
                        // collapse the overlay back when the user types
                        // the same value as the source.
                        const baseline = baselineAttribute(data, key) ?? ''
                        return async (next: string) => {
                          if (next === baseline) {
                            clearIfcEditAttribute(guidNonNull, key)
                            ifcEditApi.audit('ifc_edit.attribute.clear', {
                              guid: guidNonNull,
                              attribute: key,
                            })
                            return
                          }
                          setIfcEditAttribute(guidNonNull, key, next)
                          ifcEditApi.audit('ifc_edit.attribute.edit', {
                            guid: guidNonNull,
                            attribute: key,
                          })
                        }
                      }
                    : undefined
                }
              />
              {cotaDoMotor && (
                <div className="mt-1 px-1 text-[10px] text-[var(--color-text-dim)]">
                  {textoDaCotaDoMotor(cotaDoMotor)}
                </div>
              )}
            </Section>

            <Section
              title="Property sets"
              count={filtered.psets.length}
              defaultOpen={false}
              forceOpen={searching}
            >
              {filtered.psets.length === 0 ? (
                <div className="px-1 py-2 text-[11px] italic text-[var(--color-text-muted)]">
                  {searching
                    ? 'Nenhum property set corresponde.'
                    : 'Sem property sets.'}
                </div>
              ) : (
                filtered.psets.map((g, i) => (
                  <div
                    key={`${g.name}-${i}`}
                    className="mb-3 last:mb-0 rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40"
                  >
                    <div className="flex items-center gap-2 border-b border-[var(--color-border)]/60 px-2 py-1 text-[11px] font-medium text-[var(--color-text)]">
                      <span>{g.name}</span>
                      <span className="text-[10px] text-[var(--color-text-muted)]">
                        {g.ifcClass}
                      </span>
                      {g.viaBoQ ? <ViaBoqBadge /> : <InstanceBadge />}
                    </div>
                    <div className="px-2 pb-1">
                      <KeyValueTable
                        rows={g.rows}
                        unidadeDe={unidadeDe}
                        emptyMessage="Pset sem propriedades."
                        getChoices={
                          ifcEditEnabled && !g.viaBoQ
                            ? (key) => {
                                if (key.includes('.')) return null
                                const row = g.rows.find((r) => r.key === key)
                                if (!row) return null
                                const v = (row.value ?? '').toLowerCase()
                                if (v === 'true' || v === 'false') {
                                  return ['T', 'F', 'U']
                                }
                                return null
                              }
                            : undefined
                        }
                        getOnSave={
                          ifcEditEnabled && view.guid && !g.viaBoQ
                            ? (key) => {
                                if (key.includes('.')) return null
                                const row = g.rows.find((r) => r.key === key)
                                if (!row) return null
                                const guidNonNull = view.guid!
                                const baseline = row.value ?? ''
                                return async (next: string) => {
                                  if (next === baseline) {
                                    clearIfcEditPset(
                                      guidNonNull,
                                      g.name,
                                      key,
                                    )
                                    ifcEditApi.audit(
                                      'ifc_edit.pset.delete',
                                      {
                                        guid: guidNonNull,
                                        pset: g.name,
                                        property: key,
                                      },
                                    )
                                    return
                                  }
                                  setIfcEditPset(
                                    guidNonNull,
                                    g.name,
                                    key,
                                    next,
                                  )
                                  ifcEditApi.audit('ifc_edit.pset.write', {
                                    guid: guidNonNull,
                                    pset: g.name,
                                    property: key,
                                  })
                                }
                              }
                            : undefined
                        }
                      />
                    </div>
                  </div>
                ))
              )}
            </Section>

            <Section
              title="Quantity sets"
              count={filtered.qsets.length}
              defaultOpen={false}
              forceOpen={searching}
            >
              {filtered.qsets.length === 0 ? (
                <div className="px-1 py-2 text-[11px] italic text-[var(--color-text-muted)]">
                  {searching
                    ? 'Nenhum quantity set corresponde.'
                    : 'Sem quantity sets.'}
                </div>
              ) : (
                filtered.qsets.map((g, i) => (
                  <div
                    key={`${g.name}-${i}`}
                    className="mb-3 last:mb-0 rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40"
                  >
                    <div className="flex items-center gap-2 border-b border-[var(--color-border)]/60 px-2 py-1 text-[11px] font-medium text-[var(--color-text)]">
                      <span>{g.name}</span>
                      <span className="text-[10px] text-[var(--color-text-muted)]">
                        {g.ifcClass}
                      </span>
                      {g.viaBoQ ? <ViaBoqBadge /> : <InstanceBadge />}
                    </div>
                    <div className="px-2 pb-1">
                      <KeyValueTable
                        rows={g.rows}
                        unidadeDe={unidadeDe}
                        getOnSave={
                          ifcEditEnabled && view.guid && !g.viaBoQ
                            ? (key) => {
                                if (key.includes('.')) return null
                                const row = g.rows.find((r) => r.key === key)
                                if (!row) return null
                                const guidNonNull = view.guid!
                                const baseline = row.value ?? ''
                                return async (next: string) => {
                                  if (next === baseline) {
                                    clearIfcEditPset(
                                      guidNonNull,
                                      g.name,
                                      key,
                                    )
                                    ifcEditApi.audit(
                                      'ifc_edit.pset.delete',
                                      {
                                        guid: guidNonNull,
                                        pset: g.name,
                                        property: key,
                                      },
                                    )
                                    return
                                  }
                                  setIfcEditPset(
                                    guidNonNull,
                                    g.name,
                                    key,
                                    next,
                                  )
                                  ifcEditApi.audit('ifc_edit.pset.write', {
                                    guid: guidNonNull,
                                    pset: g.name,
                                    property: key,
                                  })
                                }
                              }
                            : undefined
                        }
                      />
                    </div>
                  </div>
                ))
              )}
            </Section>

            {/* Os Psets do type também ficam na vista SIMPLES: a instância
                herda-os, e escondê-los no modo avançado deixava metade das
                propriedades do elemento fora da vista por omissão. */}
            {view.typeInfo && view.typeInfo.localId !== null && selection && (
              <Section
                title={`Herdado do type: ${view.typeInfo.name}`}
                defaultOpen
                forceOpen={searching}
              >
                <PsetsDoType
                  modelId={selection.modelId}
                  typeId={view.typeInfo.localId}
                  nomesDaInstancia={new Set([...view.psets, ...view.qsets].map((g) => g.name))}
                  unidadeDe={unidadeDe}
                />
              </Section>
            )}

            {/* Materiais fica na vista SIMPLES: é informação de uso
                diário, ao nível dos property sets e classificações. */}
            <Section
              title="Materiais"
              count={view.materials.length}
              defaultOpen={false}
            >
              {view.materials.length === 0 ? (
                <div className="px-1 py-2 text-[11px] italic text-[var(--color-text-muted)]">
                  Sem materiais.
                </div>
              ) : (
                view.materials.map((g, i) => (
                  <div
                    key={`${g.name}-${i}`}
                    className="mb-3 last:mb-0 rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40"
                  >
                    <div className="border-b border-[var(--color-border)]/60 px-2 py-1 text-[11px] font-medium text-[var(--color-text)]">
                      {g.name}
                      <span className="ml-2 text-[10px] text-[var(--color-text-muted)]">
                        {g.ifcClass}
                      </span>
                    </div>
                    <div className="px-2 pb-1">
                      <KeyValueTable rows={g.rows} />
                    </div>
                  </div>
                ))
              )}
            </Section>

            <Section
              title="Classificações"
              count={view.classifications.length}
              defaultOpen={false}
            >
              {view.classifications.length === 0 ? (
                <div className="px-1 py-2 text-[11px] italic text-[var(--color-text-muted)]">
                  Sem classificações.
                </div>
              ) : (
                view.classifications.map((g, i) => (
                  <div
                    key={`${g.name}-${i}`}
                    className="mb-3 last:mb-0 rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40"
                  >
                    <div className="border-b border-[var(--color-border)]/60 px-2 py-1 text-[11px] font-medium text-[var(--color-text)]">
                      {g.name}
                      <span className="ml-2 text-[10px] text-[var(--color-text-muted)]">
                        {g.ifcClass}
                      </span>
                    </div>
                    <div className="px-2 pb-1">
                      <KeyValueTable rows={g.rows} />
                    </div>
                  </div>
                ))
              )}
            </Section>

            {propsAdvanced && (
            <>
            <Section
              title="Relações inversas"
              count={view.inverseRelations.length}
              defaultOpen={false}
            >
              {view.inverseRelations.length === 0 ? (
                <div className="px-1 py-2 text-[11px] italic text-[var(--color-text-muted)]">
                  Sem relações inversas neste item.
                </div>
              ) : (
                view.inverseRelations.map((rel) => (
                  <div
                    key={rel.name}
                    className="mb-3 last:mb-0 rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40"
                  >
                    <div className="flex items-center justify-between border-b border-[var(--color-border)]/60 px-2 py-1 text-[11px] font-medium text-[var(--color-text)]">
                      <span>{rel.name}</span>
                      <span className="text-[10px] text-[var(--color-text-muted)]">
                        {rel.targets.length}
                      </span>
                    </div>
                    <ul className="divide-y divide-[var(--color-border)]/40">
                      {rel.targets.map((t, i) => (
                        <InverseRelationRow
                          key={`${rel.name}-${i}-${t.localId ?? 'no-id'}`}
                          target={t}
                          modelId={selection?.modelId ?? null}
                          openPopup={openPopup}
                        />
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </Section>

            <div className="border-b border-[var(--color-border)] bg-[var(--color-bg)]/60 px-3 py-1.5 text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
              Detalhes IFC
            </div>

            <Section title="Documentos associados" defaultOpen={false}>
              <DocumentosDoElemento
                modelId={selection.modelId}
                localId={selection.localId}
              />
            </Section>

            <Section title="Cadeia de herança IFC" defaultOpen={false}>
              {view.inheritance.length === 0 ? (
                <div className="px-1 py-2 text-[11px] italic text-[var(--color-text-muted)]">
                  Sem cadeia de herança disponível.
                </div>
              ) : (
                <ol className="flex flex-col gap-0.5 pl-2 text-[11px]">
                  {view.inheritance.map((cls, i) => (
                    <li
                      key={`${cls}-${i}`}
                      className="flex items-center gap-2"
                    >
                      <span className="font-mono text-[var(--color-text)]">
                        {cls}
                      </span>
                      {i < view.inheritance.length - 1 && (
                        <span className="text-[var(--color-text-muted)]">
                          ↓
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </Section>

            <Section title="Tipo IFC (Type vs Instance)" defaultOpen={false}>
              {view.typeInfo ? (
                <div className="rounded border border-emerald-700/50 bg-[var(--color-ok-bg)] px-2 py-1.5 text-[11px] text-emerald-100">
                  <div className="mb-1 text-[10px] uppercase tracking-wide text-emerald-400/80">
                    Type-defined (parametrizado por IfcXxxType)
                  </div>
                  <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                    <span className="text-[var(--color-text-muted)]">
                      Classe
                    </span>
                    <span className="font-mono text-[var(--color-text)]">
                      {view.typeInfo.category}
                    </span>
                    <span className="text-[var(--color-text-muted)]">
                      Nome
                    </span>
                    <span className="text-[var(--color-text)]">
                      {view.typeInfo.name !== '-'
                        ? view.typeInfo.name
                        : '(sem nome)'}
                    </span>
                    <span className="text-[var(--color-text-muted)]">
                      GUID
                    </span>
                    <span className="truncate font-mono text-[10px] text-[var(--color-text)]">
                      {view.typeInfo.guid ?? 'null'}
                    </span>
                    {view.typeInfo.predefinedType !== '-' && (
                      <>
                        <span className="text-[var(--color-text-muted)]">
                          PredefinedType
                        </span>
                        <span className="font-mono text-[var(--color-text)]">
                          {view.typeInfo.predefinedType}
                        </span>
                      </>
                    )}
                    {view.typeInfo.localId !== null && (
                      <>
                        <span className="text-[var(--color-text-muted)]">
                          Local ID
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            if (view.typeInfo?.localId != null && selection) {
                              void viewerApi.selectByLocalId(
                                selection.modelId,
                                view.typeInfo.localId,
                                false,
                              )
                            }
                          }}
                          className="text-left font-mono text-[var(--color-accent)] hover:underline"
                        >
                          #{view.typeInfo.localId}
                        </button>
                      </>
                    )}
                  </div>
                  {view.typeInfo.localId !== null && (
                    <div className="mt-2 text-[10px] text-emerald-400/80">
                      Os property sets do type estão na secção Herdado do type.
                    </div>
                  )}
                </div>
              ) : !podeTerType(view.category) ? (
                // Um IfcSite, um piso ou o projecto não têm type no
                // esquema: não há nada a apontar.
                <div className="px-1 text-[11px] text-[var(--color-text-muted)]">
                  {view.category} não é definido por types no esquema IFC.
                </div>
              ) : (
                <div className="rounded border border-amber-700/50 bg-[var(--color-warn-bg)] px-2 py-1.5 text-[11px] text-[var(--color-warn-text)]">
                  <div className="mb-1 text-[10px] uppercase tracking-wide text-[var(--color-accent)]">
                    Instance-only (sem IfcXxxType)
                  </div>
                  Esta entidade não está ligada a um type via
                  IfcRelDefinesByType. As propriedades estão a viver na
                  instância. Em modelos bem parametrizados deveria haver
                  um IfcXxxType partilhado entre instâncias semelhantes.
                </div>
              )}
            </Section>

            <Section title="Geometria (IfcShapeRepresentation)" defaultOpen={false}>
              <RawRepresentationView raw={rawInfo} />
            </Section>
            </>
            )}

            <Section title="Coordenadas" defaultOpen={false}>
              <CoordinatesView
                coords={coords}
                modelInfo={modelInfo}
                showGlobal={showGlobal}
                onToggleGlobal={() => setShowGlobal((v) => !v)}
                ancestorMeta={ancestorMeta}
                modelId={selection?.modelId ?? null}
                localId={selection?.localId ?? null}
                ancestors={
                  selection
                    ? viewerApi.getAncestorPath(
                        selection.modelId,
                        selection.localId,
                      )
                    : []
                }
                elementCategory={view.category}
                elementName={view.name}
                location={view.location}
              />
            </Section>

            {propsAdvanced && (
            <Section title="JSON cru" defaultOpen={false}>
              <button
                type="button"
                onClick={() => setShowRaw((v) => !v)}
                className="mb-2 rounded border border-[var(--color-border)] px-2 py-0.5 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              >
                {showRaw ? 'Esconder JSON' : 'Mostrar JSON'}
              </button>
              {showRaw && (
                <pre className="whitespace-pre-wrap break-all rounded bg-[var(--color-bg)]/60 p-2 font-mono text-[10px] leading-relaxed text-[var(--color-text)]">
                  {safeStringify(data)}
                </pre>
              )}
            </Section>
            )}
          </>
        )}
      </div>
    </div>
  )
}

interface SummaryProps {
  targets: { modelId: string; localId: number }[]
  onClear: () => void
}

function MultiSelectionSummary({ targets, onClear }: SummaryProps) {
  // We don't fetch metadata for every target (could be hundreds);
  // present only the count and per-model totals plus a deselect
  // shortcut. Click on a target row makes it the primary via the
  // store's setSelection helper.
  const setSelection = useAppStore((s) => s.setSelection)
  const { nomeDe, varios } = useNomesDosModelos()
  const byModel = new Map<string, number>()
  for (const t of targets) {
    byModel.set(t.modelId, (byModel.get(t.modelId) ?? 0) + 1)
  }
  // Show last 8 selected for quick navigation.
  const tail = targets.slice(-8).reverse()
  return (
    <div className="border-b border-[var(--color-border)] bg-[var(--color-bg)]/60 px-3 py-2">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wide text-[var(--color-accent)]">
          Seleção múltipla
        </span>
        <button
          type="button"
          onClick={onClear}
          className="rounded border border-[var(--color-border)] px-1.5 py-0 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          Limpar
        </button>
      </div>
      <div className="text-[11px] text-[var(--color-text)]">
        {targets.length} elemento(s) selecionado(s)
        {byModel.size > 1 && (
          <>
            {' '}
            em {byModel.size} modelos:{' '}
            {[...byModel.entries()].map(([id, n], i) => {
              const nome = nomeDe(id)
              return (
                <span key={id} title={nome?.completo ?? id}>
                  {i > 0 ? ' · ' : ''}
                  {nome?.minimo ?? id} {n}
                </span>
              )
            })}
          </>
        )}
      </div>
      <div className="mt-1 text-[10px] text-[var(--color-text-muted)]">
        Últimos selecionados (clique para mostrar):
      </div>
      <ul className="mt-0.5 flex flex-wrap gap-1">
        {tail.map((t, i) => (
          <li key={`${t.modelId}-${t.localId}-${i}`}>
            <button
              type="button"
              onClick={() => setSelection(t)}
              title={nomeDe(t.modelId)?.completo ?? t.modelId}
              className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1.5 py-0 font-mono text-[10px] text-[var(--color-text)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]"
            >
              {varios && nomeDe(t.modelId) && (
                <span className="mr-1 text-[var(--color-text-muted)]">{nomeDe(t.modelId)!.minimo}</span>
              )}
              #{t.localId}
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-1 text-[10px] italic text-[var(--color-text-dim)]">
        Comparação abaixo: comum a todos vs. variável. Clique num chip para
        ver propriedades de um elemento específico.
      </div>
    </div>
  )
}

function safeStringify(root: unknown): string {
  const seen = new WeakSet<object>()
  return JSON.stringify(
    root,
    (_key, value: unknown) => {
      if (typeof value === 'bigint') return value.toString()
      if (value instanceof Map) return Object.fromEntries(value)
      if (value instanceof Set) return Array.from(value)
      if (value !== null && typeof value === 'object') {
        if (seen.has(value as object)) return '[Circular]'
        seen.add(value as object)
      }
      return value
    },
    2,
  )
}

// Single inverse-relation target row. Extracted from the inline map so
// the popup-out button can stop pointer/click propagation reliably (the
// outer floating window listens for pointerdown to bring itself to
// front, and earlier inline closures occasionally lost their click in
// detached/popup modes).
interface InverseRelationRowProps {
  target: import('@viewer/lib/ifcItemData').InverseRelationTarget
  modelId: string | null
  openPopup: (t: { modelId: string; localId: number }) => string
}

function InverseRelationRow({
  target,
  modelId,
  openPopup,
}: InverseRelationRowProps) {
  const clickable = target.localId !== null && modelId !== null
  // Single click → cumulative add (do NOT swap the panel away). Double
  // click → replace selection + zoom (the "navigate here" gesture).
  // We intentionally let onClick fire on the first half of a double
  // click; onDoubleClick then replaces, so the final state is the
  // double-click target alone. A brief frame may flash with both
  // selected, acceptable.
  const onSingleClick = () => {
    if (!clickable || modelId === null || target.localId === null) return
    useAppStore
      .getState()
      .applyBulkSelection([{ modelId, localId: target.localId }], 'add')
  }
  const onDoubleClick = () => {
    if (!clickable || modelId === null || target.localId === null) return
    void viewerApi.selectByLocalId(modelId, target.localId, true)
  }
  const onPopup = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (!clickable || modelId === null || target.localId === null) {
      console.warn(
        '[InverseRelationRow] popup ignored, clickable=' +
          clickable +
          ' modelId=' +
          String(modelId) +
          ' localId=' +
          String(target.localId),
      )
      return
    }
    const id = openPopup({ modelId, localId: target.localId })
    console.info(
      '[InverseRelationRow] openPopup id=' + id + ' for #' + target.localId,
    )
  }
  return (
    <li className="flex items-center gap-1 px-2 py-1 text-[11px]">
      <button
        type="button"
        onClick={onSingleClick}
        onDoubleClick={onDoubleClick}
        disabled={!clickable}
        className={`flex-1 truncate text-left ${
          clickable
            ? 'cursor-pointer hover:text-[var(--color-accent)]'
            : 'cursor-default text-[var(--color-text-muted)]'
        }`}
        title={
          target.guid
            ? `${target.guid} (clique: somar à selecção · duplo clique: focar)`
            : 'clique: somar à selecção · duplo clique: focar'
        }
      >
        <span className="text-[10px] uppercase text-[var(--color-text-muted)]">
          {target.category}
        </span>{' '}
        <span className="text-[var(--color-text)]">
          {target.name !== '-' ? target.name : '(sem nome)'}
        </span>
        {target.localId !== null && (
          <span className="ml-1 text-[10px] text-[var(--color-text-muted)]">
            #{target.localId}
          </span>
        )}
      </button>
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onPopup}
        disabled={!clickable}
        title="Abrir em janela flutuante"
        className="shrink-0 rounded border border-[var(--color-border)] px-1 py-0 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-30"
      >
        ↗
      </button>
    </li>
  )
}

// ---------------------------------------------------------------------------
// Multi-selection comparison view
// ---------------------------------------------------------------------------
//
// Com 2+ elementos selecionados, mostramos uma matriz: linhas =
// atributos / propriedades / quantidades, uma coluna por elemento. As
// linhas que variam entre elementos ficam realçadas. A lógica de
// comparação (pura) vive em compareElements.ts; aqui só a renderização.


// Subtle marker shown next to the pset name when the entries came
// from a BoQ session that hasn't been round-tripped through a save
// yet. Kept light on purpose: the user wants to know the provenance
// without the BoQ entries dominating the panel.
function ViaBoqBadge() {
  return (
    <span
      title="Escrito pelo BoQ nesta sessão (ainda não persistido em ficheiro)"
      className="ml-auto rounded bg-[var(--color-accent)]/15 px-1 py-px text-[9px] uppercase tracking-wide text-[var(--color-accent)]"
    >
      via BoQ
    </span>
  )
}

// Badge de origem para property/quantity sets que vêm da própria
// instância (via IfcRelDefinesByProperties do elemento), por oposição
// aos escritos pelo BoQ. Torna a proveniência de cada set explícita.
function InstanceBadge() {
  return (
    <span
      title="Property set da própria instância (lido do IfcRelDefinesByProperties do elemento)"
      className="ml-auto rounded bg-[var(--color-bg)] px-1 py-px text-[9px] uppercase tracking-wide text-[var(--color-text-dim)]"
    >
      instância
    </span>
  )
}

export default PropertiesPanel
